import React, { useState } from 'react';

import { Button } from '@eika/button';
import { InfoCard } from '@eika/card';
import { RichIcon, RichIconTypes } from '@eika/rich-icon';
import { SearchSelect } from '@eika/search-select';

import { BlockEfakturaCreditor } from '../../components/BlockEfakturaCreditor';
import { EFakturaReservationList } from '../../components/EFakturaReservationList';
import { EikaModal } from '../../components/EikaModal';
import { Content } from '../../components/shared/Content';
import { ErrorCardWrapper } from '../../components/shared/ErrorCardWrapper';
import { useEfakturaCreditors } from '../../fetchHooks/useEfaktura';
import type { EFakturaCreditor as Creditor } from '../../generated-types';
import useTranslation from '../../hooks/useTranslation';
import { globalCssNamespace } from '../../index';
import EFakturaReservationsHeader from './EFakturaReservationsHeader';
import EFakturaReservationsSkeleton from './EFakturaReservationsSkeleton';

import './EFakturaReservations.scss';

const EFakturaReservations: React.FC = () => {
    const translated = useTranslation();
    const [chosenCreditor, setChosenCreditor] = useState<Creditor | undefined>(undefined);
    const [showCreditorModal, setShowCreditorModal] = useState<boolean>(false);
    const [blocked, setBlocked] = useState(false);
    const [searchString, setSearchString] = useState('');
    const { data: creditors, error, isLoading, refetch, invalidateQuery } = useEfakturaCreditors();

    const onCloseModal = () => {
        setChosenCreditor(undefined);
        if (blocked) {
            setSearchString('');
            invalidateQuery();
        }
        setBlocked(false);
    };

    if (isLoading) {
        return <EFakturaReservationsSkeleton />;
    }

    if (error) {
        return (
            <Content>
                <ErrorCardWrapper
                    title={translated.errors.somethingWentWrong}
                    exception={error}
                    defaultError={translated.eFakturaReservation.error}
                    isModal
                >
                    <Button onClick={() => refetch()}>{translated.tryAgain}</Button>
                </ErrorCardWrapper>
            </Content>
        );
    }
    if (!creditors.length) {
        return (
            <>
                <EFakturaReservationsHeader />
                <Content>
                    <InfoCard skipBackground>
                        <RichIcon type={RichIconTypes.SOMMERFUGLER} />
                        <InfoCard.TitleH2>{translated.eFakturaReservation.noCreditors}</InfoCard.TitleH2>
                    </InfoCard>
                </Content>
            </>
        );
    }

    return (
        <>
            <EFakturaReservationsHeader />
            <Content>
                <SearchSelect
                    appNamespace={globalCssNamespace}
                    suggestList={creditors.map((creditor) => {
                        return { value: creditor.name };
                    })}
                    value={searchString}
                    list="efaktura-reservations-search-list"
                    onInputChange={(e) => {
                        setSearchString(e.target.value);
                    }}
                    onButtonSelect={(value) => {
                        const creditor = creditors.find((creditor) => value === creditor.name);
                        setChosenCreditor(creditor);
                    }}
                    useSearchInput
                    onSuccess={() => setShowCreditorModal(true)}
                />
            </Content>

            <EikaModal
                id="block-efaktura-creditor-modal"
                open={showCreditorModal}
                setOpen={setShowCreditorModal}
                onClose={onCloseModal}
                timeOut={blocked ? 2500 : undefined}
                contentShouldStretch
            >
                {chosenCreditor && <BlockEfakturaCreditor creditor={chosenCreditor} setBlocked={setBlocked} />}
            </EikaModal>

            <Content className="efaktura-reservations__reservation-list">
                <EFakturaReservationList creditors={creditors} />
            </Content>
        </>
    );
};

export default EFakturaReservations;
